import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { DollarSign, TrendingUp, Upload, ChevronDown, RefreshCw, Sliders, Search, Users, Target, ArrowUpRight, ArrowDownRight, AlertTriangle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import LiveIndicator from '../ui/LiveIndicator';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) => v == null ? '—' : `$${Math.round(v).toLocaleString('en-AU')}`;
const fmtK = (v) => v == null ? '—' : v >= 1_000_000 ? `$${(v / 1_000_000).toFixed(2)}M` : `$${Math.round(v / 1000)}k`;

// Revenue Tracker replaces the manual spreadsheet.  Multi-FY support via a
// dropdown; each FY has its own actuals + forecast, computed at read-time so
// forecast-config changes don't require re-uploading.
const RevenueTracker = () => {
  const [years, setYears] = useState([]);
  const [selectedFy, setSelectedFy] = useState(null);
  const [fyData, setFyData] = useState(null);
  const [compare, setCompare] = useState(null);
  const [runRate, setRunRate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [managerFilter, setManagerFilter] = useState('all');
  const [uploading, setUploading] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  const loadYears = useCallback(async () => {
    const res = await axios.get(`${API}/revenue-tracker/years`);
    setYears(res.data.years);
    if (!selectedFy && res.data.years.length) setSelectedFy(res.data.years[0].fy);
  }, [selectedFy]);

  const loadFy = useCallback(async (fy) => {
    if (!fy) return;
    setLoading(true);
    try {
      const [detail, cmp, rr] = await Promise.all([
        axios.get(`${API}/revenue-tracker/${fy}`),
        axios.get(`${API}/revenue-tracker/${fy}/compare`).catch(() => ({ data: null })),
        axios.get(`${API}/revenue-tracker/${fy}/run-rate`).catch(() => ({ data: null })),
      ]);
      setFyData(detail.data);
      setCompare(cmp?.data || null);
      setRunRate(rr?.data || null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadYears(); }, [loadYears]);
  useEffect(() => { if (selectedFy) loadFy(selectedFy); }, [selectedFy, loadFy]);

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await axios.post(`${API}/upload/revenue-tracker`, fd);
      await loadYears();
      setSelectedFy(res.data.fy);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const saveConfig = async (patch) => {
    if (!fyData) return;
    setSavingConfig(true);
    try {
      const next = { ...fyData.config, ...patch };
      await axios.post(`${API}/revenue-tracker/${selectedFy}/forecast-config`, next);
      await loadFy(selectedFy);
    } finally { setSavingConfig(false); }
  };

  // Filter plans for the table
  const filteredPlans = useMemo(() => {
    if (!fyData) return [];
    const q = search.trim().toLowerCase();
    return fyData.plans.filter(p => {
      if (managerFilter !== 'all' && p.ocmEofy !== managerFilter) return false;
      if (!q) return true;
      return (p.planNumber || '').toLowerCase().includes(q)
        || (p.propertyName || '').toLowerCase().includes(q)
        || (p.ocmEofy || '').toLowerCase().includes(q);
    });
  }, [fyData, search, managerFilter]);

  const managers = useMemo(() => {
    if (!fyData) return [];
    return Array.from(new Set(fyData.plans.map(p => p.ocmEofy).filter(Boolean))).sort();
  }, [fyData]);

  if (loading && !fyData) {
    return (
      <div className="p-8 text-center text-[#737373]" data-testid="revenue-tracker-loading">
        <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-3 text-[#A3A3A3]" />
        Loading revenue tracker…
      </div>
    );
  }

  if (!years.length) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center bg-white border border-dashed border-[#E5E5E5] rounded-[14px]" data-testid="revenue-tracker-empty">
        <DollarSign className="w-12 h-12 text-[#A3A3A3] mx-auto mb-4" />
        <h2 className="text-xl font-bold text-[#171717] mb-2">Revenue Tracker</h2>
        <p className="text-sm text-[#525252] mb-6">Upload your <em>Revenue Report v2</em> workbook to start tracking actuals + forecast per financial year.</p>
        <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#171717] text-white text-sm font-semibold cursor-pointer hover:bg-black">
          <Upload className="w-4 h-4" />
          {uploading ? 'Uploading…' : 'Upload workbook'}
          <input type="file" accept=".xlsx,.xlsm" onChange={handleUpload} className="hidden" data-testid="revenue-upload-input" />
        </label>
      </div>
    );
  }

  const cfg = fyData?.config || { mode: 'straight_line', upliftPct: 0 };
  const chartData = fyData?.streamTotals.map(s => ({ name: s.label.replace('OC ', ''), Actual: s.actual, Forecast: s.forecast })) || [];

  return (
    <div className="space-y-6" data-testid="revenue-tracker">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-[10px] p-6 bg-white border border-[#E5E5E5]" style={{ boxShadow: 'var(--card-shadow)' }}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#171717] flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#A3A3A3] mb-1">Financial Year Actuals + Forecast</p>
              <h1 className="text-xl font-bold text-[#171717] font-heading tracking-tight">Revenue Tracker</h1>
              <p className="text-sm text-[#737373] mt-1 max-w-2xl leading-relaxed">
                Live per-plan × per-stream revenue with forecast projected across the FY. Replaces the manual Revenue Report spreadsheet.
              </p>
              <LiveIndicator className="mt-2" updatedAt={fyData?.uploadedAt} />
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <select
                data-testid="fy-selector"
                value={selectedFy || ''}
                onChange={(e) => setSelectedFy(e.target.value)}
                className="appearance-none pl-3 pr-8 py-2 rounded-lg bg-[#F5F5F5] border border-[#E5E5E5] text-[#171717] text-sm font-semibold focus:outline-none focus:border-[#171717]"
              >
                {years.map(y => <option key={y.fy} value={y.fy}>{y.fy}</option>)}
              </select>
              <ChevronDown className="w-3.5 h-3.5 text-[#A3A3A3] absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
            <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-[#E5E5E5] text-sm font-semibold text-[#525252] hover:border-[#171717] cursor-pointer">
              <Upload className="w-3.5 h-3.5" />
              {uploading ? 'Uploading…' : 'Upload'}
              <input type="file" accept=".xlsx,.xlsm" onChange={handleUpload} className="hidden" data-testid="revenue-upload-input" />
            </label>
          </div>
        </div>

        {/* Forecast config strip */}
        <div className="mt-5 pt-4 border-t border-[#E5E5E5] flex items-center gap-3 flex-wrap">
          <Sliders className="w-3.5 h-3.5 text-[#A3A3A3]" />
          <p className="text-[11px] text-[#737373]">Forecast mode:</p>
          <select
            data-testid="forecast-mode"
            value={cfg.mode}
            onChange={(e) => saveConfig({ mode: e.target.value })}
            disabled={savingConfig}
            className="px-2 py-1 rounded-md bg-[#FAFAFA] border border-[#E5E5E5] text-[#171717] text-xs focus:outline-none focus:border-[#171717]"
          >
            <option value="straight_line">Straight-line (annualise days-managed)</option>
            <option value="historical_uplift">Prior FY × uplift %</option>
            <option value="manual">Manual (per-plan overrides only)</option>
          </select>
          {cfg.mode === 'historical_uplift' && (
            <>
              <p className="text-[11px] text-[#737373]">Uplift</p>
              <input
                data-testid="uplift-pct"
                type="number"
                step="0.5"
                value={cfg.upliftPct}
                onChange={(e) => saveConfig({ upliftPct: parseFloat(e.target.value) || 0 })}
                className="w-16 px-2 py-1 rounded-md bg-[#FAFAFA] border border-[#E5E5E5] text-[#171717] text-xs text-center focus:outline-none focus:border-[#171717]"
              />
              <p className="text-[11px] text-[#737373]">%</p>
            </>
          )}
          <div className="w-px h-4 bg-[#E5E5E5] mx-1" />
          <Target className="w-3.5 h-3.5 text-[#A3A3A3]" />
          <p className="text-[11px] text-[#737373]">Budget:</p>
          <p className="text-[11px] text-[#525252]">Prior FY ×</p>
          <input
            data-testid="budget-uplift-pct"
            type="number"
            step="0.5"
            value={cfg.budgetUpliftPct ?? 3}
            onChange={(e) => saveConfig({ budgetUpliftPct: parseFloat(e.target.value) || 0 })}
            className="w-16 px-2 py-1 rounded-md bg-[#FAFAFA] border border-[#E5E5E5] text-[#171717] text-xs text-center focus:outline-none focus:border-[#171717]"
          />
          <p className="text-[11px] text-[#737373]">% uplift</p>
          <p className="text-[10px] text-[#A3A3A3] ml-auto">
            {fyData.updateDate ? `Actuals updated ${fyData.updateDate}` : ''}
            {fyData.updateDate && ` · `}
            {`${fyData.planCount} plans · FY runs ${fyData.fyStart} → ${fyData.fyEnd}`}
          </p>
        </div>
      </div>

      {/* Summary KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="fy-summary-tiles">
        <SummaryTile label="Total Actual" value={fmtK(fyData.totalActual)} sub="year-to-date" tone="dark" testId="tile-actual" accent="dark" />
        <SummaryTile label="Total Forecast" value={fmtK(fyData.totalForecast)} sub={`for ${fyData.fy}`} tone="emerald" testId="tile-forecast" accent="emerald" />
        <SummaryTile
          label="Actual vs Forecast"
          value={`${Math.round((fyData.totalActual / (fyData.totalForecast || 1)) * 100)}%`}
          sub={`gap ${fmtK(fyData.totalForecast - fyData.totalActual)}`}
          tone="amber"
          testId="tile-gap"
          accent="amber"
        />
        <SummaryTile
          label={compare?.prior ? `YoY vs ${compare.prior.fy}` : 'YoY'}
          value={compare?.prior?.actual
            ? `${(((fyData.totalForecast - compare.prior.actual) / compare.prior.actual) * 100).toFixed(1)}%`
            : '—'}
          sub={compare?.prior?.actual ? `${compare.prior.fy}: ${fmtK(compare.prior.actual)} actual` : 'no prior year uploaded'}
          tone="blue"
          testId="tile-yoy"
          accent="blue"
        />
      </div>

      {/* Quarterly Run-Rate + YoY pace */}
      <RunRateSection runRate={runRate} fy={fyData.fy} />

      {/* Unmapped plans warning — revenue withheld until they're added to the master list */}
      {fyData.unmappedPlanCount > 0 && (
        <UnmappedWarning
          count={fyData.unmappedPlanCount}
          revenue={fyData.unmappedRevenue}
          plans={fyData.unmappedPlans}
          fy={fyData.fy}
        />
      )}

      {/* Stream chart */}
      <div className="bg-white border border-[#E5E5E5] rounded-[10px] p-6" style={{ boxShadow: 'var(--card-shadow)' }}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[15px] font-bold text-[#171717] font-heading">Revenue by stream</h2>
          <p className="text-[11px] text-[#737373]">Actual bars in charcoal · Forecast in emerald</p>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
            <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#737373' }} interval={0} angle={-25} textAnchor="end" height={70} />
            <YAxis tick={{ fontSize: 10, fill: '#737373' }} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
            <Tooltip formatter={(v) => fmt(v)} contentStyle={{ fontSize: 11 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="Actual" fill="#171717" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Forecast" fill="#10B981" radius={[4, 4, 0, 0]} fillOpacity={0.65} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Per-manager rollup */}
      <div className="bg-white border border-[#E5E5E5] rounded-[10px]" style={{ boxShadow: 'var(--card-shadow)' }}>
        <div className="px-6 py-4 border-b border-[#E5E5E5] flex items-center gap-3">
          <Users className="w-4 h-4 text-[#525252]" />
          <h2 className="text-[15px] font-bold text-[#171717] font-heading">Attributed to OC Managers</h2>
          <p className="text-[11px] text-[#737373] ml-1">Pro-rated by days-managed (EOFY + OCM 1 + OCM 2)</p>
        </div>
        <div className="overflow-x-auto max-h-[400px]" data-testid="ocm-rollup-table">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] sticky top-0">
              <tr>
                <th className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">OC Manager</th>
                <th className="text-right px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Plans</th>
                <th className="text-right px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Days</th>
                <th className="text-right px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Actual</th>
                <th className="text-right px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Forecast</th>
              </tr>
            </thead>
            <tbody>
              {fyData.ocmRollup.map(r => (
                <tr key={r.ocm} className="border-t border-[#F0F0F0] hover:bg-[#FAFAFA]/50">
                  <td className="px-4 py-2 font-medium text-[#171717]">{r.ocm}</td>
                  <td className="px-4 py-2 text-right text-[#525252]">{r.planCount}</td>
                  <td className="px-4 py-2 text-right text-[#525252]">{Math.round(r.daysManaged).toLocaleString()}</td>
                  <td className="px-4 py-2 text-right font-mono text-[#171717]">{fmt(r.actualRevenue)}</td>
                  <td className="px-4 py-2 text-right font-mono font-bold text-emerald-700">{fmt(r.forecastRevenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Per-plan drill-in */}
      <div className="bg-white border border-[#E5E5E5] rounded-[10px]" style={{ boxShadow: 'var(--card-shadow)' }}>
        <div className="px-6 py-4 border-b border-[#E5E5E5] flex items-center gap-3 flex-wrap">
          <TrendingUp className="w-4 h-4 text-[#525252]" />
          <h2 className="text-[15px] font-bold text-[#171717] font-heading">Per-plan revenue</h2>
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-[#A3A3A3] absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                data-testid="plan-search"
                placeholder="Search plan / property / manager"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 pr-3 py-1.5 rounded-lg bg-[#FAFAFA] border border-[#E5E5E5] text-[#171717] text-xs w-72 focus:outline-none focus:border-[#171717]"
              />
            </div>
            <select
              data-testid="manager-filter"
              value={managerFilter}
              onChange={(e) => setManagerFilter(e.target.value)}
              className="px-2 py-1.5 rounded-lg bg-[#FAFAFA] border border-[#E5E5E5] text-[#171717] text-xs focus:outline-none focus:border-[#171717]"
            >
              <option value="all">All managers</option>
              {managers.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>
        <div className="overflow-x-auto max-h-[600px]" data-testid="per-plan-table">
          <table className="w-full text-xs">
            <thead className="bg-[#FAFAFA] sticky top-0">
              <tr>
                <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Plan</th>
                <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Property</th>
                <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">OCM EOFY</th>
                <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Lots</th>
                <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-[#171717]">Actual</th>
                <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-emerald-700">Forecast</th>
                {fyData.streamKeys.map(k => (
                  <th key={k} className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">{fyData.streamLabels[k].replace('OC ','').replace(' Revenue','')}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredPlans.slice(0, 300).map(p => (
                <tr key={p.planNumber} className="border-t border-[#F0F0F0] hover:bg-[#FAFAFA]/50">
                  <td className="px-3 py-1.5 font-mono font-semibold text-[#171717]">{p.planNumber}</td>
                  <td className="px-3 py-1.5 text-[#525252] max-w-[180px] truncate" title={p.propertyName}>{p.propertyName || '—'}</td>
                  <td className="px-3 py-1.5 text-[#525252]">{p.ocmEofy || '—'}</td>
                  <td className="px-3 py-1.5 text-right text-[#525252]">{p.occupiableLots || '—'}</td>
                  <td className="px-3 py-1.5 text-right font-mono font-semibold text-[#171717]">{fmt(p.total)}</td>
                  <td className="px-3 py-1.5 text-right font-mono font-bold text-emerald-700">{fmt(p.totalForecast)}</td>
                  {fyData.streamKeys.map(k => (
                    <td key={k} className="px-3 py-1.5 text-right font-mono text-[#525252]">{p.streams[k] ? Math.round(p.streams[k]).toLocaleString() : '·'}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {filteredPlans.length > 300 && (
            <p className="px-3 py-2 text-[11px] text-[#A3A3A3] italic">Showing first 300 of {filteredPlans.length} · refine search to narrow.</p>
          )}
        </div>
      </div>
    </div>
  );
};

const SummaryTile = ({ label, value, sub, tone = 'dark', testId, accent }) => {
  const bg = { dark: 'bg-[#171717] text-white', emerald: 'bg-emerald-50 text-emerald-800 border border-emerald-200', amber: 'bg-amber-50 text-amber-900 border border-amber-200', blue: 'bg-sky-50 text-sky-800 border border-sky-200' }[tone];
  const accentColor = { emerald: 'bg-emerald-500', amber: 'bg-amber-500', blue: 'bg-sky-500', red: 'bg-rose-500', dark: 'bg-[#171717]' }[accent];
  return (
    <div data-testid={testId} className={`relative rounded-[10px] p-5 overflow-hidden ${bg}`} style={{ boxShadow: 'var(--card-shadow)' }}>
      {accent && <span className={`absolute left-0 top-0 bottom-0 w-1 ${accentColor}`} />}
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] opacity-70">{label}</p>
      <p className="text-2xl font-bold font-heading mt-1">{value}</p>
      <p className="text-[11px] opacity-70 mt-1">{sub}</p>
    </div>
  );
};

// Quarterly run-rate + YoY pace strip.  Shows a RAG per quarter (green if
// cumulative actual ≥ pro-rata budget target, amber within 5%, red beyond).
// Only the current + past quarters have a real actual number; future
// quarters render pending.
const RunRateSection = ({ runRate, fy }) => {
  if (!runRate) return null;
  const noBudget = !runRate.totalBudget || runRate.totalBudget === 0;

  return (
    <div className="bg-white border border-[#E5E5E5] rounded-[10px] p-6" style={{ boxShadow: 'var(--card-shadow)' }} data-testid="run-rate-section">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <h2 className="text-[15px] font-bold text-[#171717] font-heading">Quarterly Run-Rate vs Budget · YoY Pace</h2>
          <p className="text-[11px] text-[#737373] mt-0.5">
            Budget = Prior FY × ({runRate.config?.budgetUpliftPct ?? 3}% uplift).
            {' '}As of <span className="font-mono">{runRate.asOf}</span> · Day {runRate.daysElapsed} of {runRate.daysInFy} · Current: <span className="font-semibold text-[#171717]">{runRate.currentQuarter || '—'}</span>.
          </p>
        </div>
        {noBudget && (
          <p className="text-[11px] px-3 py-1.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200" data-testid="no-budget-warning">
            Upload a prior FY workbook to activate budget targets.
          </p>
        )}
      </div>

      {/* Quarterly tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="quarter-tiles">
        {runRate.quarters.map(q => <QuarterTile key={q.label} q={q} fy={fy} noBudget={noBudget} />)}
      </div>

      {/* YoY pace tile */}
      <YoYPaceTile yoy={runRate.yoy} currentActual={runRate.totalActual} />
    </div>
  );
};

const QuarterTile = ({ q, fy, noBudget }) => {
  const ragCfg = {
    green:   { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800', chip: 'bg-emerald-100 text-emerald-800', label: 'On Track' },
    amber:   { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-900',   chip: 'bg-amber-100 text-amber-800',   label: 'Behind' },
    red:     { bg: 'bg-rose-50',    border: 'border-rose-200',    text: 'text-rose-800',    chip: 'bg-rose-100 text-rose-800',     label: 'At Risk' },
    pending: { bg: 'bg-[#FAFAFA]',  border: 'border-[#E5E5E5]',   text: 'text-[#525252]',   chip: 'bg-[#F0F0F0] text-[#737373]',   label: 'Pending' },
  }[q.rag] || {};
  const activeRing = q.isCurrent ? 'ring-2 ring-[#171717]' : '';

  return (
    <div
      data-testid={`quarter-tile-${q.label.toLowerCase()}`}
      className={`rounded-[10px] p-4 border ${ragCfg.bg} ${ragCfg.border} ${activeRing}`}
    >
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] font-bold uppercase tracking-wider text-[#171717]">
          {q.label}
          {q.isCurrent && <span className="ml-1.5 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-[#171717] text-white">NOW</span>}
        </p>
        {!noBudget && (
          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${ragCfg.chip}`}>{ragCfg.label}</span>
        )}
      </div>
      {q.cumulativeActual !== null && q.cumulativeActual !== undefined ? (
        <>
          <p className={`text-xl font-bold font-heading ${ragCfg.text}`}>{fmtK(q.cumulativeActual)}</p>
          <p className="text-[10px] text-[#737373] mt-0.5">
            Target {fmtK(q.budgetProRata)}
            {q.variancePct !== null && ` · ${q.variancePct >= 0 ? '+' : ''}${q.variancePct}%`}
          </p>
        </>
      ) : (
        <>
          <p className="text-xl font-bold font-heading text-[#A3A3A3]">—</p>
          <p className="text-[10px] text-[#737373] mt-0.5">Target {fmtK(q.budgetFullQuarter)}</p>
        </>
      )}
    </div>
  );
};

const YoYPaceTile = ({ yoy, currentActual }) => {
  if (!yoy) {
    return (
      <div className="rounded-[10px] p-4 bg-[#FAFAFA] border border-dashed border-[#E5E5E5] text-center" data-testid="yoy-empty">
        <p className="text-[11px] text-[#737373]">Upload a prior FY workbook to see YoY pace.</p>
      </div>
    );
  }
  const onTrack = yoy.onTrack;
  const Icon = onTrack ? ArrowUpRight : ArrowDownRight;
  const bg = onTrack ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200';
  const text = onTrack ? 'text-emerald-800' : 'text-rose-800';

  return (
    <div className={`rounded-[10px] p-4 border ${bg} grid grid-cols-1 md:grid-cols-4 gap-4`} data-testid="yoy-pace-tile">
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#737373]">YoY same-period pace</p>
        <p className={`text-lg font-bold font-heading ${text} mt-0.5 flex items-center gap-1.5`}>
          <Icon className="w-4 h-4" />
          {yoy.deltaPct !== null ? `${yoy.deltaPct >= 0 ? '+' : ''}${yoy.deltaPct}%` : '—'}
          <span className="text-[10px] font-medium text-[#737373] ml-1">{yoy.priorFy}</span>
        </p>
      </div>
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#737373]">This FY (YTD)</p>
        <p className="text-lg font-bold text-[#171717] mt-0.5">{fmtK(currentActual)}</p>
      </div>
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#737373]">{yoy.priorFy} at same pace</p>
        <p className="text-lg font-semibold text-[#525252] mt-0.5">{fmtK(yoy.priorAtCurrentPace)}</p>
      </div>
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#737373]">Delta $</p>
        <p className={`text-lg font-bold ${text} mt-0.5`}>{yoy.delta >= 0 ? '+' : ''}{fmtK(yoy.delta)}</p>
        <p className="text-[10px] text-[#737373]">
          Full-year {yoy.fullYearDeltaPct !== null && `${yoy.fullYearDeltaPct >= 0 ? '+' : ''}${yoy.fullYearDeltaPct}%`} vs {fmtK(yoy.priorFullYearActual)}
        </p>
      </div>
    </div>
  );
};

export default RevenueTracker;

// Warning strip shown on Revenue Tracker + Revenue Detail when the FY
// workbook contains plans that aren't in the master plan list yet.  Their
// revenue is withheld from every rollup until mapped — we surface the list
// here (and mirror it on Data Quality) so users know what dollars they're
// leaving on the table.
const UnmappedWarning = ({ count, revenue, plans, fy }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div
      data-testid="unmapped-plans-warning"
      className="bg-orange-50 border border-orange-200 rounded-[10px] p-4"
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="w-4 h-4 text-orange-700" strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-orange-900">
            {count} plan{count === 1 ? '' : 's'} not yet on the master list — revenue excluded from {fy} totals
          </p>
          <p className="text-xs text-orange-800/80 mt-0.5">
            {revenue > 0
              ? `$${Math.round(revenue).toLocaleString('en-AU')} of revenue is being withheld until these plans are added to the Master List Configuration.`
              : 'Revenue in the workbook for these plans is $0 today, but they still need to be onboarded before they roll into any total.'}
          </p>
        </div>
        <button
          data-testid="unmapped-plans-toggle"
          onClick={() => setOpen(o => !o)}
          className="text-xs font-semibold px-3 py-1.5 rounded-md bg-white text-orange-800 border border-orange-200 hover:bg-orange-100 flex-shrink-0"
        >
          {open ? 'Hide list' : 'View list'}
        </button>
      </div>
      {open && (
        <div className="mt-3 pt-3 border-t border-orange-200 max-h-[280px] overflow-y-auto" data-testid="unmapped-plans-list">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-orange-800/70">
                <th className="text-left px-2 py-1.5">Plan</th>
                <th className="text-left px-2 py-1.5">Property</th>
                <th className="text-left px-2 py-1.5">Workbook Manager</th>
                <th className="text-right px-2 py-1.5">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {plans.map(p => (
                <tr key={p.planNumber} className="border-t border-orange-100">
                  <td className="px-2 py-1 font-mono font-semibold text-orange-900">{p.planNumber}</td>
                  <td className="px-2 py-1 text-orange-900/80 truncate max-w-[220px]">{p.propertyName || '—'}</td>
                  <td className="px-2 py-1 text-orange-900/80">{p.manager || '—'}</td>
                  <td className="px-2 py-1 text-right font-mono text-orange-900">
                    {p.total ? `$${Math.round(p.total).toLocaleString('en-AU')}` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
